import { useEffect, useState } from 'react'
import { BellRing, Download } from 'lucide-react'
import {
  Button, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui'
import { BrandMark } from '@/components/common/BrandMark'
import { enablePush, getPushState } from '@/lib/push'
import { INSTALL_GUIDE, isStandalone, whereToLook } from '@/lib/install'
import { Emphasised } from '@/components/common/Emphasised'
import { env } from '@/lib/env'

/**
 * Two prompts, in order, on phones only:
 *
 *   1. Install the app, if it is running in a browser tab.
 *   2. Turn on notifications, once it is installed.
 *
 * Notifications are asked for second on purpose. On iOS push does not work at
 * all until the app is on the home screen, so asking first would burn the one
 * permission prompt the browser gives you on a request that cannot succeed —
 * and a denied permission is painful to recover from.
 */
interface InstallEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

const INSTALL_DISMISSED = 'mymdv.install.dismissed'
const NOTIFY_DISMISSED = 'mymdv.notify.dismissed'
/** Long enough not to nag, short enough that a new phone gets asked again. */
const SNOOZE_DAYS = 30

/** The copy marks the name of a button as `**like this**`. */
function isPhone(): boolean {
  return window.matchMedia?.('(max-width: 1024px)').matches || /android|iphone|ipad|ipod/i.test(navigator.userAgent)
}

function snoozed(key: string): boolean {
  try {
    const until = window.localStorage.getItem(key)
    return until !== null && Number(until) > Date.now()
  } catch {
    return false
  }
}

function snooze(key: string): void {
  try {
    window.localStorage.setItem(key, String(Date.now() + SNOOZE_DAYS * 86400_000))
  } catch {
    // Private browsing — it will simply ask again next time.
  }
}

export function MobileOnboarding() {
  const [step, setStep] = useState<'none' | 'install' | 'notify'>('none')
  const [deferred, setDeferred] = useState<InstallEvent | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (env.useMockApi || !isPhone()) return

    function onBeforeInstall(event: Event) {
      event.preventDefault()
      setDeferred(event as InstallEvent)
    }
    window.addEventListener('beforeinstallprompt', onBeforeInstall)

    // Deliberately delayed: appearing over a half-painted screen reads as a
    // pop-up ad rather than part of the app.
    const timer = window.setTimeout(async () => {
      if (!isStandalone()) {
        if (!snoozed(INSTALL_DISMISSED)) setStep('install')
        return
      }

      // Installed — now notifications are worth asking about.
      if (snoozed(NOTIFY_DISMISSED)) return
      if (Notification.permission === 'granted' || Notification.permission === 'denied') return

      const state = await getPushState()
      if (state.configured && !state.registered) setStep('notify')
    }, 2500)

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall)
      window.clearTimeout(timer)
    }
  }, [])

  function close(key: string) {
    snooze(key)
    setStep('none')
  }

  async function install() {
    if (!deferred) return
    setBusy(true)
    await deferred.prompt()
    const choice = await deferred.userChoice
    setBusy(false)
    setDeferred(null)
    if (choice.outcome === 'accepted') setStep('none')
    else close(INSTALL_DISMISSED)
  }

  async function turnOnNotifications() {
    setBusy(true)
    setError(null)
    const failure = await enablePush()
    setBusy(false)
    if (failure) {
      setError(failure)
      return
    }
    setStep('none')
  }

  if (step === 'none') return null

  if (step === 'install') {
    /*
     * Three states, not two: a real button when the browser offers one,
     * written instructions when it does not, and nothing at all once the app
     * is installed (the effect above never opens this step then).
     *
     * The instructions are per browser because the alternative is naming a
     * button that is not there. See lib/install.ts.
     */
    const { title, steps } = INSTALL_GUIDE[whereToLook()]
    return (
      <Dialog open onOpenChange={() => close(INSTALL_DISMISSED)}>
        <DialogContent className="max-h-[92dvh] grid-rows-[auto_minmax(0,1fr)_auto] overflow-hidden rounded-2xl p-6 sm:p-7">
          <DialogHeader>
            <div className="mb-3 flex justify-center">
              <BrandMark size="xl" />
            </div>
            <DialogTitle className="text-center text-xl">Install MDV Services</DialogTitle>
            <DialogDescription className="text-center text-base leading-relaxed">
              Put it on your home screen for faster loading, and so it can tell you when
              you are scheduled.
            </DialogDescription>
          </DialogHeader>

          {deferred ? (
            <p className="rounded-lg border border-border p-4 text-sm text-muted-foreground">
              Tap <strong className="font-semibold text-foreground">Install</strong> below. It
              takes a second, uses almost no space, and you can remove it like any other app.
            </p>
          ) : (
            <div className="min-h-0 space-y-3 overflow-y-auto scrollbar-thin">
              <p className="text-sm font-medium">{title}</p>
              <ol className="space-y-3 rounded-lg border border-border p-4 text-sm">
                {steps.map(([instruction, detail], i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold">
                      {i + 1}
                    </span>
                    <span className="min-w-0">
                      <span className="block leading-snug">
                        <Emphasised text={instruction} />
                      </span>
                      <span className="mt-1 block text-xs leading-snug text-muted-foreground">
                        <Emphasised text={detail} />
                      </span>
                    </span>
                  </li>
                ))}
              </ol>
            </div>
          )}

          <DialogFooter>
            <Button variant="ghost" onClick={() => close(INSTALL_DISMISSED)}>
              Not now
            </Button>
            {deferred ? (
              <Button onClick={() => void install()} loading={busy}>
                <Download className="h-4 w-4" />
                Install
              </Button>
            ) : (
              <Button onClick={() => close(INSTALL_DISMISSED)}>Got it</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    )
  }

  return (
    <Dialog open onOpenChange={() => close(NOTIFY_DISMISSED)}>
      <DialogContent className="rounded-2xl p-6 sm:p-7">
        <DialogHeader>
          <div className="mb-4 flex justify-center">
            <span className="flex h-20 w-20 items-center justify-center rounded-full bg-primary/10">
              <BellRing className="h-10 w-10 text-primary" aria-hidden />
            </span>
          </div>
          <DialogTitle className="text-center text-xl">Turn on notifications</DialogTitle>
          <DialogDescription className="text-center text-base leading-relaxed">
            Get told when you are scheduled, when a plan changes, and when someone needs an
            answer — without opening the app.
          </DialogDescription>
        </DialogHeader>

        <p className="rounded-lg border border-border p-3 text-xs text-muted-foreground">
          Your phone will ask for permission next. You can change it any time from your
          account menu.
        </p>

        {error ? <p className="text-sm text-destructive">{error}</p> : null}

        <DialogFooter>
          <Button variant="ghost" onClick={() => close(NOTIFY_DISMISSED)}>
            Not now
          </Button>
          <Button onClick={() => void turnOnNotifications()} loading={busy}>
            <BellRing className="h-4 w-4" />
            Turn on
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
